import type {MenuItemProps} from '@chakra-ui/react';
import {Box, Icon, MenuItem} from '@chakra-ui/react';
import type {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import type {FC, JSX} from 'react';
import {forwardRef, isValidElement, useMemo} from 'react';

import type {IconType} from '../../icons';
import {useContextMenuStyleConfig} from '../../theme-config';
import {ChakraI} from '../../utils/chakra-elements';
import {Label} from '../Label';

type Props = {
  label?: string;
  sublabel?: string;
  icon?: IconType | JSX.Element;
  ref?: React.Ref<HTMLButtonElement>;
} & Omit<MenuItemProps, 'icon'>;

const ContextMenuItem: FC<Props> = ({label, ref, icon, key, sublabel, children, ...props}) => {
  const style = useContextMenuStyleConfig(`item`, props);
  const iconStyle = useContextMenuStyleConfig(`icon`, props);
  const resolvedIcon = useMemo(() => {
    if (!icon) return null;

    if (isValidElement(icon) && isFontAwesomeIcon(icon)) {
      return icon;
    } else {
      return <Icon as={icon as IconType} />;
    }
  }, [icon]);

  return (
    // We do the icon spacing using the theme
    <MenuItem
      {...props}
      ref={ref}
      iconSpacing={0}
      key={key}
      icon={resolvedIcon ? <ChakraI sx={iconStyle}>{resolvedIcon}</ChakraI> : undefined}
      sx={style}
    >
      {children
        ? children
        : (sublabel && (
            <Box>
              {label}
              <Label size={`sm`} color={`text.secondary`} fontWeight={`normal`}>
                {sublabel}
              </Label>
            </Box>
          )) ||
          label}
    </MenuItem>
  );
};

export default forwardRef<HTMLButtonElement, Props>((props, ref) => <ContextMenuItem {...props} ref={ref} />);
export type {Props as ContextMenuItemProps};

ContextMenuItem.displayName = `ContextMenuItem`;

const isFontAwesomeIcon = (icon: any): icon is typeof FontAwesomeIcon => {
  const prefix = icon?.props?.icon?.prefix;
  return prefix === `fal` || prefix === `fas` || prefix === `far` || prefix === `fad` || prefix === `fab`;
};
